import type { Messages } from '@/i18n/types';

export const en: Messages = {
  meta: {
    title: 'Fustar — Think · See · Local-first AI knowledge hub',
    description:
      'A local-first AI knowledge hub: files, agents, and plugins, in one place, all made visible. Runs locally; your data never leaves your device.',
  },
  header: {
    tagline: 'Local-first AI knowledge hub',
  },
  language: {
    switchLabel: 'Language',
    zh: '中文',
    en: 'EN',
  },
  theme: {
    switchToLight: 'Switch to light theme',
    switchToDark: 'Switch to dark theme',
  },
  hero: {
    brandTitle: 'Think · See',
    subtitle:
      'A local-first AI knowledge hub — files are first-class, agents orchestrate plugins, outcomes made visible.',
    featureTags: ['Local-first', 'Files first-class', 'Everything is a plugin', 'Agent-driven plugins'],
    downloadCta: 'Get started',
    exploreCta: 'See how it works',
    previewLabel: 'Preview',
  },
  navigation: {
    workspace: 'Home',
    capabilities: 'Capabilities',
    demos: 'Use cases',
    stats: 'Stats',
    faq: 'FAQ',
    download: 'Download',
  },
  useCaseDemos: {
    eyebrow: 'Everything is a plugin',
    title: 'Compose on demand—scenarios you define',
    description:
      'Fustar extends analysis and views through Fusion Hub plugins. Install what your scenario needs—Fustar designs, organizes, and implements the workflow, then preserves outcomes in your workspace.',
    sortLabel: 'Sort',
    videoPlaceholder: 'Watch demo',
    sortOptions: {
      featured: 'Featured',
      plugin: 'By plugin type',
      recent: 'Latest',
    },
    items: {
      'cohort-retention': {
        category: 'Data analysis',
        title: 'User retention and churn analysis',
        description:
          'Import retention.csv; Agent calls the DuckDB plugin to aggregate D+7 activation and preserves charts and conclusions as document blocks.',
        author: 'Growth analytics',
      },
      'pdf-semantic-search': {
        category: 'Doc search',
        title: 'Semantic search and citations in long PDFs',
        description:
          'With the semantic search plugin, ask natural-language questions over hundreds of pages—answers include source passages you can paste into documents.',
        author: 'Knowledge mgmt',
      },
      'sql-explore-visualize': {
        category: 'SQL explore',
        title: 'Query data in natural language and generate charts',
        description:
          'Describe the metrics you want in plain language; Agent queries data locally and renders interactive charts, all traceable in the document.',
        author: 'Data explore',
      },
      'embed-files': {
        category: 'Knowledge hub',
        title: 'Embed any file in your documents',
        description:
          'Embed documents, 3D, tables, or code into the same document; Agent references, converses, and produces results—blurring the line between documents and files.',
        author: 'Knowledge hub',
      },
      'interactive-dashboard': {
        category: 'Visualization',
        title: 'Interactive sales dashboard',
        description:
          'The widget contract renders query results as refreshable chart blocks—filter, drill down, rerun with new params, all traceable.',
        author: 'Business views',
      },
      'research-corpus': {
        category: 'App development',
        title: 'From idea to runnable app',
        description:
          'Describe requirements in natural language; Agent writes code and invokes build plugins, compiling ideas into runnable apps and documents.',
        author: 'Coding assist',
      },
      'file-format-convert': {
        category: 'Format convert',
        title: 'Automatic file format conversion',
        description:
          'Drop CSV, Markdown, JSON, images, and more into your document; Agent auto-detects and converts formats, ready to reuse or export.',
        author: 'Format convert',
      },
      'web-data-query': {
        category: 'Web query',
        title: 'Web data query',
        description:
          'Agent calls the web search plugin to fetch real-time information and writes sourced results into your document—traceable and verifiable.',
        author: 'Web search',
      },
      'agent-orchestration': {
        category: 'Agent orchestration',
        title: 'Automated multi-step task orchestration',
        description:
          'Describe the goal in natural language; Agent chains load, clean, compute, and chart plugins—each step and result written back to the document.',
        author: 'Workflow',
      },
      'hub-plugin-install': {
        category: 'Plugin ecosystem',
        title: 'Install domain plugins from the Hub',
        description:
          'Open Fusion Hub, browse compute and visualization plugins, install in one click, then call them from Agent and the command palette to extend your scenarios.',
        author: 'Fusion Hub',
      },
    },
  },
  workspace: {
    eyebrow: 'From document to outcome',
    title: 'Write documents, run Agent, and preserve outcomes in one workspace.',
    description:
      'Sources, task briefs, and Agent orchestration live in one workspace. Every analysis can be reopened, verified, and extended.',
    previewLabel: 'Interactive preview',
    runCta: 'Run Agent',
    resetCta: 'Run again',
    readyStatus: 'Ready to run',
    computingStatus: 'Agent orchestrating locally',
    storedStatus: 'Saved to document',
    visualLabel: 'Visualize',
    computeLabel: 'Compute',
    storageLabel: 'Preserve',
    queryLabel: 'Analysis task',
    query: 'Compare D+7 activation by cohort and find common signals before churn',
    tableLabel: 'Result data',
    noteLabel: 'Working document',
    noteTitle: 'User retention analysis',
    noteBody: 'Trace activation in the first seven days and find the common signals before churn.',
    agentLabel: 'Agent task',
    agentTitle: 'Orchestrating analysis and charts',
    agentBody:
      'Parse the task goal, invoke the tool chain, verify results, and write the process back to the document.',
    insightLabel: 'Key finding',
    insightTitle: 'Activation rate',
    insightMetric: '+18.4%',
    insightDetail: 'People who complete a first project retain better after 30 days.',
    dataSource: 'retention.csv · 12.4k rows',
    computeLogs: ['Load cohort table', 'Aggregate D+7 activation', 'Write vision block'],
    sidebarNotes: 'Documents',
    sidebarSources: 'Sources',
    sidebarProjects: 'Projects',
  },
  capabilities: {
    eyebrow: 'Capabilities',
    title: 'See, compute, and keep—all in one workspace.',
    description:
      'From drafting to Agent orchestration to preserved outcomes—a workflow that continues; each output becomes the starting point for the next analysis.',
    items: [
      {
        eyebrow: 'Visualize',
        title: 'Any carrier, interactive views',
        description:
          'Documents, tables, audio/video, and scientific views—whatever the carrier, turn it into interactive visual blocks that reveal structure and relationships.',
      },
      {
        eyebrow: 'Compute',
        title: 'Compute and orchestrate locally',
        description:
          'Drive Agent in natural language—from SQL exploration and statistics to Hub domain plugins—keeping the process and parameters alongside the result, not just an answer.',
      },
      {
        eyebrow: 'Store',
        title: 'Results as living blocks',
        description:
          'Charts, interactive tables, and conclusions stay in your project as editable blocks you can reopen, edit, reproduce, and keep growing.',
      },
    ],
  },
  stats: {
    eyebrow: 'Data & Metrics',
    title: 'Lightweight, fast, and private by design',
    description:
      'Fustar runs locally, your data never leaves your device, while delivering outstanding performance.',
    items: [
      {
        value: '< 150 MB',
        label: 'Install size',
        description: 'Compact desktop client, downloads in seconds',
      },
      {
        value: 'Local-first',
        label: 'Full desktop experience',
        description:
          'Workspace, attachment analysis, and Hub compute run on-device in the desktop app; the web preview is feature-limited',
      },
      {
        value: 'Any format',
        label: 'File formats',
        description: 'Supports CSV, Parquet, JSON, Markdown and more',
      },
      {
        value: 'Plugin',
        label: 'Extensible',
        description: 'Extend analysis and visualization via the Hub plugin marketplace',
      },
    ],
  },
  download: {
    eyebrow: 'Start from your desktop',
    title: 'A complete workspace for knowledge work.',
    description:
      'Fustar is available for Windows, macOS, Linux, and the web. Pick the way you want to begin.',
  },
  openSource: {
    eyebrow: 'Traceable · Open ecosystem',
    title: 'Traceable process, plugins opening gradually',
    description:
      'Every step of your analysis in Fustar is traceable—computation steps and parameters are preserved alongside results. Compute and visualization plugins on Fusion Hub are planned for gradual open-source release, so the community can build and audit together. Your data always stays local, never used for training or uploaded to third parties.',
    badge: 'Plugin open-source roadmap',
    cta: 'Follow on GitHub',
  },
  platforms: {
    windows: { label: 'Windows', hint: 'Windows 10+' },
    'macos-arm64': { label: 'macOS', hint: 'Apple Silicon (M series)' },
    'macos-x64': { label: 'macOS', hint: 'Intel' },
    linux: { label: 'Linux', hint: 'AppImage (arm64)' },
    web: { label: 'Use online', hint: 'Browser' },
  },
  platformsHover: {
    download: 'Download',
    open: 'Open',
  },
  features: [
    {
      id: 'local',
      title: 'Files are first-class citizens',
      paragraphs: [
        'Documents, tables, code, 3D, data—any file type enters the same editor, carried and conversed with as one.',
        'Not “uploaded elsewhere to process”—files live on your workbench as first-class objects you can compute over, converse with, and preserve.',
      ],
    },
    {
      id: 'agent',
      title: 'One agent, orchestrating every plugin',
      paragraphs: [
        'Reads your intent, calls the right plugin, and breaks down, executes, and delivers complex tasks—the plugin hub solves every scenario.',
        'Agent is the super AI center, composing plugins on demand so every expression becomes a tangible result.',
      ],
    },
    {
      id: 'vision',
      title: 'Outcomes preserved, reproducible',
      paragraphs: [
        'Every conversation’s result lands back in the editor as reusable documents and assets—not one-off chat.',
        'Process, parameters, and conclusions are kept together, ready to reopen, verify, and extend into growing knowledge.',
      ],
    },
  ],
  illustrations: {
    local: 'Illustration: local server and on-device data',
    agent: 'Illustration: Agent orchestration and outcomes written back to documents',
    vision: 'Illustration: working documents and block preservation',
  },
  faq: {
    eyebrow: 'Frequently Asked Questions',
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Is Fustar completely free to use? Are there any feature limitations?',
        answer:
          'Fustar is in Alpha—try everything for free today. After launch, core features stay free for individuals: documents, workspace, and local Agent orchestration, with no paywall for everyday writing and light analysis. Cloud model calls and heavier compute draw from a monthly free allowance that should cover typical personal use; go beyond that and you can upgrade. Team and enterprise plans are in the works—get in touch via the site if you need them.',
      },
      {
        question: 'Are my local data and privacy protected in Fustar?',
        answer:
          'Fustar is built local-first. On the **desktop app**, workspace files are not uploaded to the cloud by default; attachment analysis and Hub compute run in local environments on your device, and we do not use your files for model training. The **online preview** runs in the browser without full local filesystem access, container compute, or some Agent tools—it is meant for preview and editing. For privacy-sensitive, fully local workflows, use the desktop app.',
      },
      {
        question: 'What data and file formats does Fustar natively support?',
        answer:
          'In the current Alpha, common data and document attachments include CSV, Parquet, TSV, JSON, Markdown, plain text, PDF, and images (PNG/JPEG/SVG/WebP). What you can do depends on installed Hub plugins and Agent mode (e.g. DuckDB for structured tables, semantic search for long PDF/Markdown). Excel and similar formats usually need a plugin or conversion to CSV/Markdown first. Very large files depend on plugin and device limits; we expand format support over releases.',
      },
      {
        question: "In what ways can I customize and extend Fustar's capabilities?",
        answer:
          'The main supported path today is **Fusion Hub plugins**: install compute or visualization plugins from the Hub, then call them from Agent and the command palette. Chart output uses the widget contract to render blocks in documents. On desktop, optional **AI compute engine (container)** runs heavier plugins after in-app setup. Custom widgets, a public command-palette API, and more no-code composition are on the roadmap—for Alpha, start with Hub plugins.',
      },
    ],
  },
  waitlist: {
    eyebrow: 'Stay Tuned',
    title: 'Get Early Access',
    description:
      'Sign in with your email verification code to join the waitlist for updates and beta access.',
    placeholder: 'Enter your email',
    codePlaceholder: 'Enter verification code',
    sendCodeButton: 'Send code',
    verifyButton: 'Join waitlist',
    resendCode: 'Resend code',
    sending: 'Sending…',
    verifying: 'Verifying…',
    error: 'Something went wrong. Check your email and code, then try again.',
    success: "You're on the list!",
  },
  footer: {
    privacy:
      'Raw data and local analysis stay on your device. We do not use your files to train models.',
  },
};
